import type { JSX } from "react";

import { TelegramIcon } from "../integrations/TelegramIcon";

export type BrandKey = "google" | "telegram" | "x" | "linkedin";

export interface BrandMarkProps {
  readonly brand: BrandKey;
  readonly size?: number;
}

/**
 * Circular brand glyph shared by the Extensions catalog and the
 * Settings → Add Account picker. Extend by adding a new case below.
 */
export function BrandMark({ brand, size = 36 }: BrandMarkProps): JSX.Element {
  switch (brand) {
    case "google":
      return <GoogleMark size={size} />;
    case "telegram":
      return <TelegramMark size={size} />;
    case "x":
      return <XMark size={size} />;
    case "linkedin":
      return <LinkedInMark size={size} />;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function resolveBrand(id: string): BrandKey | undefined {
  if (id === "google" || id === "mock-gmail") return "google";
  if (id === "telegram" || id === "mock-telegram") return "telegram";
  if (id === "x" || id === "mock-x") return "x";
  if (id === "anysite" || id === "linkedin" || id === "mock-linkedin") return "linkedin";
  return undefined;
}

function GoogleMark({ size }: { readonly size: number }): JSX.Element {
  const inner = Math.round(size * 0.56);
  return (
    <div
      className="flex items-center justify-center rounded-full bg-white shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={inner} height={inner} viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
    </div>
  );
}

function TelegramMark({ size }: { readonly size: number }): JSX.Element {
  const inner = Math.round(size * 0.6);
  return (
    <div
      className="flex items-center justify-center rounded-full shrink-0 text-white"
      style={{ width: size, height: size, backgroundColor: "#2AABEE" }}
    >
      <TelegramIcon size={inner} />
    </div>
  );
}

// Official X (Twitter) mark: white glyph on black.
function XMark({ size }: { readonly size: number }): JSX.Element {
  const inner = Math.round(size * 0.5);
  return (
    <div
      className="flex items-center justify-center rounded-full shrink-0"
      style={{ width: size, height: size, backgroundColor: "#000000" }}
    >
      <svg width={inner} height={inner} viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"
          fill="#ffffff"
        />
      </svg>
    </div>
  );
}

// Official LinkedIn mark: white "in" glyph on LinkedIn blue.
function LinkedInMark({ size }: { readonly size: number }): JSX.Element {
  const inner = Math.round(size * 0.56);
  return (
    <div
      className="flex items-center justify-center rounded-full shrink-0"
      style={{ width: size, height: size, backgroundColor: "#0A66C2" }}
    >
      <svg width={inner} height={inner} viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4.98 3.5C4.98 4.881 3.87 6 2.5 6S.02 4.881.02 3.5C.02 2.12 1.13 1 2.5 1s2.48 1.12 2.48 2.5zM5 8H0v16h5V8zm7.982 0H8.014v16h4.966v-8.399c0-4.67 6.029-5.052 6.029 0V24H24V13.869c0-7.88-8.922-7.593-11.018-3.714V8z"
          fill="#ffffff"
        />
      </svg>
    </div>
  );
}
